import styled from 'styled-components';
import type { FleetCategory, FleetStatus } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { IconDownload, IconPlus, IconSearch, IconUpload } from '@shared/ui/icons';
import {
  CheckLabel,
  FLEET_STATUS_LABEL,
  FLEET_UNIT_STATUSES,
  SegmentButton,
  Segmented,
  fleetLeading,
  fleetType,
  formatCount,
  isFleetStatus,
} from '@entities/fleet';
import { TextButton } from './ui';

/**
 * Над таблицей две строки. Первая — фильтры: что показывать. Вторая — итог по
 * отбору, вид списка и действия справа, прямо над таблицей, к которой они
 * относятся. На узком экране всё переносится и растягивается на всю ширину.
 */

const NARROW = '640px';

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Search = styled.div`
  position: relative;
  flex: 1 1 260px;
  min-width: 0;

  & > svg {
    position: absolute;
    left: ${({ theme }) => theme.spacing(3)};
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.text.secondary};
    pointer-events: none;
  }
`;

const SearchInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing(9)};
`;

const FilterSelect = styled(Select)<{ $basis: number }>`
  flex: 0 1 ${({ $basis }) => `${$basis}px`};
  width: auto;
  min-width: 0;

  @container fleet (max-width: ${NARROW}) {
    flex: 1 1 100%;
  }
`;

/** «Сбросить» в строке фильтров: коротко, чтобы строка не переносилась. */
const ResetButton = styled(TextButton)`
  min-height: 40px;
  padding: ${({ theme }) => `0 ${theme.spacing(1)}`};
  font-size: ${fleetType.body};
  white-space: nowrap;
`;

const ResultBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const Count = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const StaleToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-height: 28px;
  padding: ${({ theme }) => `0 ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.status.warning}73;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.small};
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.warning};
  }

  &:hover,
  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.colors.status.warning}1F;
  }

  &[aria-pressed='true'] {
    font-weight: 600;
  }
`;

const ActionsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-left: auto;

  @container fleet (max-width: ${NARROW}) {
    flex: 1 1 100%;
    margin-left: 0;

    & > button {
      flex: 1 1 220px;
    }
  }
`;

export type RegistryView = 'grouped' | 'flat';

interface FiltersProps {
  search: string;
  onSearch: (value: string) => void;
  categories: readonly FleetCategory[];
  categoryId: string;
  onCategory: (id: string) => void;
  status: FleetStatus | '';
  onStatus: (status: FleetStatus | '') => void;
  showArchived: boolean;
  onShowArchived: (value: boolean) => void;
  filtered: boolean;
  onReset: () => void;
}

export function RegistryFilters({
  search,
  onSearch,
  categories,
  categoryId,
  onCategory,
  status,
  onStatus,
  showArchived,
  onShowArchived,
  filtered,
  onReset,
}: FiltersProps) {
  return (
    <FilterBar role="search" aria-label="Фильтры справочника техники">
      <Search>
        <IconSearch size={16} />
        <SearchInput
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Модель, собственник, должность"
          aria-label="Поиск по названию, собственнику и должности механизатора"
          autoComplete="off"
          spellCheck={false}
        />
      </Search>
      <FilterSelect
        $basis={230}
        value={categoryId}
        onChange={(e) => onCategory(e.target.value)}
        aria-label="Категория техники"
      >
        <option value="">Все категории</option>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} · {formatCount(c.models_count)}
          </option>
        ))}
      </FilterSelect>
      <FilterSelect
        $basis={170}
        value={status}
        onChange={(e) => {
          const v = e.target.value;
          onStatus(isFleetStatus(v) ? v : '');
        }}
        aria-label="Статус"
      >
        <option value="">Любой статус</option>
        {FLEET_UNIT_STATUSES.map((s) => (
          <option key={s} value={s}>
            {FLEET_STATUS_LABEL[s]}
          </option>
        ))}
      </FilterSelect>
      <CheckLabel>
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => onShowArchived(e.target.checked)}
        />
        Показать архив
      </CheckLabel>
      {filtered && (
        <ResetButton type="button" onClick={onReset} aria-label="Сбросить фильтры">
          Сбросить
        </ResetButton>
      )}
    </FilterBar>
  );
}

interface ResultsProps {
  models: number;
  units: number;
  stale: number;
  filtered: boolean;
  staleOnly: boolean;
  onStaleOnly: (value: boolean) => void;
  view: RegistryView;
  onView: (view: RegistryView) => void;
  canWrite: boolean;
  onImport: () => void;
  onCreate: () => void;
  /** Выгрузка доступна всем, кто видит справочник. */
  onExport: () => void;
  exporting: boolean;
}

export function RegistryResults({
  models,
  units,
  stale,
  filtered,
  staleOnly,
  onStaleOnly,
  view,
  onView,
  canWrite,
  onImport,
  onCreate,
  onExport,
  exporting,
}: ResultsProps) {
  return (
    <ResultBar>
      <Count aria-live="polite">
        {filtered && 'Найдено: '}
        <strong>
          {formatCount(models)} {pluralizeRu(models, ['модель', 'модели', 'моделей'])}
        </strong>
        {' · '}
        {formatCount(units)} {pluralizeRu(units, ['единица', 'единицы', 'единиц'])}
      </Count>
      {(stale > 0 || staleOnly) && (
        <StaleToggle
          type="button"
          aria-pressed={staleOnly}
          title="Показать только модели, у которых исходные данные ПРЦ изменились после утверждения"
          onClick={() => onStaleOnly(!staleOnly)}
        >
          ПРЦ устарела · {formatCount(stale)}
        </StaleToggle>
      )}
      <Segmented role="group" aria-label="Вид списка">
        <SegmentButton
          type="button"
          aria-pressed={view === 'grouped'}
          onClick={() => onView('grouped')}
        >
          По категориям
        </SegmentButton>
        <SegmentButton type="button" aria-pressed={view === 'flat'} onClick={() => onView('flat')}>
          Списком
        </SegmentButton>
      </Segmented>
      <ActionsGroup>
        <Button
          type="button"
          $variant="ghost"
          disabled={exporting}
          title="Справочник в формате листа «Справочник (паспорт техники)»: поправить в Excel и загрузить обратно"
          onClick={onExport}
        >
          <IconDownload size={16} />
          {exporting ? 'Выгружаю…' : 'Выгрузить в Excel'}
        </Button>
        {canWrite && (
          <>
            <Button type="button" $variant="secondary" onClick={onImport}>
              <IconUpload size={16} />
              Загрузить из Excel
            </Button>
            <Button type="button" $variant="primary" onClick={onCreate}>
              <IconPlus size={16} />
              Добавить модель
            </Button>
          </>
        )}
      </ActionsGroup>
    </ResultBar>
  );
}
